import React, { useContext, useMemo, useState } from 'react';

import { Legend, LegendItem } from '@/components/shared/Legend';
import { LineChart, LineChartData } from '@/components/shared/LineChart';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { TeamSongThumbs } from '@/components/TeamSongThumbs';
import { BibleVerse } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useDarkMode } from '@/hooks/useDarkMode';
import { SetlistsContext } from '@/providers/SetlistsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import { GenerateSetlistContext } from '../../GenerateSetlist';
import AnalysisCard from '../AnalysisCard/AnalysisCard';
import AnalysisGateCard from '../AnalysisGateCard/AnalysisGateCard';
import styles from './FeedbackLineChart.module.scss';

export type LimitedBibleVerse = Pick<BibleVerse, 'reference' | 'content'> & {
	analysisOutput: NonNullable<BibleVerse['analysisOutput']>;
};

const TREND_COLOR = '#0040ff';

const DEFAULT_LAYERS = [
	'grid', 'markers', 'axes', 'areas', 'crosshair', 'lines', 'points', 'slices', 'mesh', 'legends',
] as const;

const demoDataMap = {
	'0-0': 4.5,
	'0-1': 3.2,
	'0-2': 4.0,
	'0-3': 4.9,
	'0-4': 4.7,
	'1-0': 4.2,
	'1-1': 4.1,
	'1-2': 4.6,
	'1-3': 4.1,
	'1-4': 5,
	'2-0': 4.4,
	'2-1': 3.1,
	'2-2': 4.1,
	'2-3': 4.7,
	'2-4': 4.5,
};

const FeedbackLineChart: React.FC<{
	setlist: SetlistsContext['setlists'][0];
}> = ({ setlist }) => {
	const { findTeamSongsById } = useTeamSongs();
	const { setFeedbackSetlistId } = useContext(GenerateSetlistContext);
	const [hoveredSerieId, setHoveredSerieId] = useState<string | null>(null);

	const setlistTeamSongs = findTeamSongsById(setlist.teamSongIds);

	const reports = setlist.feedback?.teamSongFeedbackReports || [];
	const reportsOrdered = setlistTeamSongs.map((teamSong) => {
		return reports.find((r) => r.teamSongId === teamSong?.id);
	});
	const personalScores = {
		id: 'personal',
		data: reportsOrdered.map((r, i) => ({
			x: r?.teamSongId || 3,
			y: r?.meanPersonalScore || 3,
		})),
	};
	const teamScores = {
		id: 'team',
		data: reportsOrdered.map((r, i) => ({
			x: r?.teamSongId || 3,
			y: r?.meanTeamScore || 3,
		})),
	};
	const congregationalScores = {
		id: 'congregation',
		data: reportsOrdered.map((r, i) => ({
			x: r?.teamSongId || 3,
			y: r?.meanCongregationalScore || 3,
		})),
	};

	const lineData = [congregationalScores, teamScores, personalScores];

	const trendLine = useMemo(() => {
		const n = lineData[0].data.length;
		if (n < 2) return null;
		const ys = lineData[0].data.map((_, i) =>
			((lineData[0].data[i].y as number) + (lineData[1].data[i].y as number) + (lineData[2].data[i].y as number)) / 3,
		);
		const sumX = (n * (n - 1)) / 2;
		const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
		const sumY = ys.reduce((a, b) => a + b, 0);
		const sumXY = ys.reduce((acc, y, i) => acc + i * y, 0);
		const denom = n * sumX2 - sumX * sumX;
		if (denom === 0) return null;
		const slope = (n * sumXY - sumX * sumY) / denom;
		const intercept = (sumY - slope * sumX) / n;
		return {
			startX: lineData[0].data[0].x,
			startY: intercept,
			endX: lineData[0].data[n - 1].x,
			endY: slope * (n - 1) + intercept,
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reports]);

	const trendLayer = useMemo(() => {
		if (!trendLine) return null;
		const { startX, startY, endX, endY } = trendLine;
		const dimmed = hoveredSerieId !== null && hoveredSerieId !== 'trend';
		return function TrendLineLayer({ xScale, yScale }: { xScale: (v: unknown) => number; yScale: (v: unknown) => number }) {
			return (
				<line
					x1={xScale(startX)}
					y1={yScale(startY)}
					x2={xScale(endX)}
					y2={yScale(endY)}
					stroke={dimmed ? `rgba(0,64,255,0.15)` : TREND_COLOR}
					strokeWidth={2}
				/>
			);
		};
	}, [trendLine, hoveredSerieId]);

	const layers = useMemo(() => {
		if (!trendLayer) return [...DEFAULT_LAYERS];
		const result = [...DEFAULT_LAYERS] as any[];
		const insertBefore = hoveredSerieId === 'trend' ? 'slices' : 'lines';
		result.splice(DEFAULT_LAYERS.indexOf(insertBefore), 0, trendLayer);
		return result;
	}, [trendLayer, hoveredSerieId]);

	const demoLineData = lineData.map((d, di) => ({
		...d,
		data: Array.from(Array(5)).map((_, si) => ({
			x: si,
			y: demoDataMap[`${di}-${si}`],
		})),
	}));

	return reports.length > 0 ? (
		<AnalysisCard
			title="Feedback summary"
			responsiveArea={
				<TeamSongThumbs
					teamSongIds={setlist.teamSongIds}
					style={{
						marginLeft: 13,
						justifyContent:
							setlist.teamSongIds.length === 1
								? 'center'
								: 'space-between',
					}}
				/>
			}
		>
			<CustomLineChart lineData={lineData} highlightedSerieId={hoveredSerieId} layers={layers} />
			<Legend className={styles['legend']}>
				<LegendItem
					label={'Personal'}
					className="px-color-scheme-red"
					onMouseEnter={() => setHoveredSerieId('personal')}
					onMouseLeave={() => setHoveredSerieId(null)}
					dimmed={hoveredSerieId !== null && hoveredSerieId !== 'personal'}
				/>
				<LegendItem
					label={'Team'}
					className="px-color-scheme-green"
					onMouseEnter={() => setHoveredSerieId('team')}
					onMouseLeave={() => setHoveredSerieId(null)}
					dimmed={hoveredSerieId !== null && hoveredSerieId !== 'team'}
				/>
				<LegendItem
					label={'Congregation'}
					className="px-color-scheme-purple"
					onMouseEnter={() => setHoveredSerieId('congregation')}
					onMouseLeave={() => setHoveredSerieId(null)}
					dimmed={hoveredSerieId !== null && hoveredSerieId !== 'congregation'}
				/>
				{trendLine && (
					<LegendItem
						label={'Trend'}
						className=""
						circleColor={TREND_COLOR}
						onMouseEnter={() => setHoveredSerieId('trend')}
						onMouseLeave={() => setHoveredSerieId(null)}
						dimmed={hoveredSerieId !== null && hoveredSerieId !== 'trend'}
					/>
				)}
			</Legend>
		</AnalysisCard>
	) : (
		<AnalysisCard title="Feedback summary">
			<div className={styles['wrapper']}>
				<CustomLineChart className="mt-lg" lineData={demoLineData} />
				<AnalysisGateCard
					body="Add your feedback to get a summary"
					ctaLabel="Give Feedback"
					onCtaClick={() => setFeedbackSetlistId(setlist.id)}
				/>
			</div>
		</AnalysisCard>
	);
};

export default FeedbackLineChart;

function CustomLineChart({
	lineData,
	className,
	highlightedSerieId,
	layers,
}: {
	lineData: LineChartData;
	className?: string;
	highlightedSerieId?: string | null;
	layers?: readonly any[];
}) {
	const { isDarkModeOn } = useDarkMode();

	return (
		<LineChart
			data={lineData}
			emojiYAxis
			wrapperClassName={classNames([
				className,
				'mt-sm',
				styles['line-chart'],
			])}
			margin={{
				bottom: 14,
				right: 5,
				top: 14,
				left: 55,
			}}
			min={1}
			noAxisBottom
			highlightedSerieId={highlightedSerieId}
			layers={layers as any}
			enableGridY={false}
			enableGridX={true}
			gridXValues={lineData[0]?.data.map((d) => d.x).filter((x): x is NonNullable<typeof x> => x != null)}
			theme={{
				grid: {
					line: {
						strokeDasharray: '0',
					},
				},
			}}
		/>
	);
}
